import { CrisisNoticeCard, type CrisisNotice } from '../components/CrisisNotice.js';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { VoiceRecorder, type Recording } from '../components/VoiceRecorder.js';
import { api, ApiError } from '../lib/api.js';
import { useAi } from '../lib/ai.js';

/**
 * Journalling — speak or type, same destination.
 *
 * Speaking is the default because it is the lower-friction path when executive
 * function is spent, which is exactly when this is worth doing and least likely
 * to happen. Typing is one tap away for when speaking isn't possible.
 */
export function Journal(): JSX.Element {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'speak' | 'write'>('speak');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crisis, setCrisis] = useState<CrisisNotice | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  /*
   * A recording that did not save, kept until it does or he lets it go.
   *
   * The recorder hands over its audio once and resets, so a failed upload lost
   * the recording outright: say it all again or not at all. The likeliest
   * failure is the one this screen invites — yesterday picked before 4am, the
   * recording reaching the server after it — so that one gets its own way out:
   * the same recording, under today.
   */
  const [unsaved, setUnsaved] = useState<{ rec: Recording; dayFinished: boolean } | null>(null);

  const { canTranscribe, canSummarise, turnedOff } = useAi();

  async function saveRecording(rec: Recording, underToday = false): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const entry = await api.uploadRecording(rec.blob, {
        kind: 'daily_journal',
        durationSeconds: rec.durationSeconds,
        // Same rule as the typed entry: only inside the grace, only when he
        // picked yesterday. This was missing entirely, so a journal spoken at
        // 12:45am went to the new day whatever the screen said.
        ...(!underToday && filedUnder && filedUnder !== todayKey ? { day: filedUnder } : {}),
      });
      navigate(`/journal/${entry.id}`);
    } catch (err) {
      const dayFinished = err instanceof ApiError && err.status === 409;
      setUnsaved({ rec, dayFinished });
      setError(
        dayFinished
          ? "That day finished before your recording reached the app, so it wasn't filed under it. Your recording is still here."
          : err instanceof ApiError
            ? `${err.message} Your recording is still here.`
            : "Couldn't save that recording. It's still here.",
      );
      setBusy(false);
    }
  }

  /*
   * Which day this belongs to, asked only between midnight and 4am.
   *
   * A journal written at 12:37 is the end of Saturday, not the start of Sunday,
   * and the app already knows it: Home falls back to yesterday's fold before
   * 4am because a day fifteen minutes old has nothing to read. It knew that
   * everywhere except where it wrote things down.
   *
   * A two-way choice rather than a date picker, and absent outside those four
   * hours: at 9pm on a Tuesday there is no question to ask, and a control that
   * asks one anyway is a decision taxed on every entry.
   */
  const todayKey = new Date().toLocaleDateString('en-CA');
  const lateNight = new Date().getHours() < 4;
  const yesterdayKey = ((): string => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toLocaleDateString('en-CA');
  })();
  const [filedUnder, setFiledUnder] = useState<string | null>(null);

  async function saveText(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const entry = await api.createTextEntry({
        text: text.trim(),
        // Only ever set inside the grace, and only when he picked yesterday.
        ...(filedUnder && filedUnder !== todayKey ? { day: filedUnder } : {}),
      });
      /*
       * Held, not navigated past.
       *
       * The server flags this write and returns the numbers with it, and this
       * screen used to drop them on the floor: somebody typed the hardest
       * sentence of their life, pressed Save, and was taken to a page showing
       * their own words back with a Delete button. The check-in was given the
       * card and its two siblings were not, which is the same one-call-site
       * failure the check itself had before today.
       */
      if (entry.crisis) {
        setSavedId(entry.id);
        setCrisis(entry.crisis);
        setBusy(false);
        return;
      }
      navigate(`/journal/${entry.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that.");
      setBusy(false);
    }
  }

  if (crisis && savedId) {
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <h1>Saved</h1>
        </header>
        <CrisisNoticeCard notice={crisis} onDismiss={() => navigate(`/journal/${savedId}`)} />
      </div>
    );
  }

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() => navigate('/')}
        >
          <ArrowLeftIcon /> Cancel
        </button>
        <h1>How was today?</h1>
        <p>
          {mode === 'speak'
            ? 'Talk for as long or as little as you want. Nobody hears this but you.'
            : 'Write as much or as little as you want.'}
        </p>
      </header>

      <div className="chips">
        <button className="chip" aria-pressed={mode === 'speak'} onClick={() => setMode('speak')}>
          Speak
        </button>
        <button className="chip" aria-pressed={mode === 'write'} onClick={() => setMode('write')}>
          Write
        </button>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {/*
        ABOVE THE MODE SPLIT, and this is the whole fix.

        It used to live inside the `write` branch, so it only ever appeared to
        somebody typing — while `mode` defaults to 'speak'. The default path was
        the broken one and the fallback was the one that worked, which is why it
        went unnoticed: every typed late-night entry filed correctly.

        It also has to come BEFORE the recorder, not after it. Speaking saves
        and navigates away the instant the recording stops, so there is no
        moment afterwards in which to ask — the question has to be already
        answered by the time he stops talking.
      */}
      {lateNight && (
        <div className="stack-sm stack">
          <p className="faint" style={{ margin: 0 }}>
            It's {clockNow()}. Which day is this?
          </p>
          <div className="chips day-choice">
            <button
              className={`btn btn-compact ${filedUnder === yesterdayKey ? 'btn-secondary' : 'btn-ghost'}`}
              onClick={() => setFiledUnder(yesterdayKey)}
            >
              {dayName(yesterdayKey)}
            </button>
            <button
              className={`btn btn-compact ${filedUnder === null || filedUnder === todayKey ? 'btn-secondary' : 'btn-ghost'}`}
              onClick={() => setFiledUnder(todayKey)}
            >
              {dayName(todayKey)}
            </button>
          </div>
        </div>
      )}

      {mode === 'speak' ? (
        <>
          {unsaved && (
            <section className="card stack-sm stack" aria-label="Recording not saved yet">
              <button
                className="btn btn-primary btn-block"
                onClick={() => {
                  const { rec, dayFinished } = unsaved;
                  if (dayFinished) setFiledUnder(null);
                  // A save navigates away; a failure puts it back here with the reason.
                  void saveRecording(rec, dayFinished);
                }}
                disabled={busy}
              >
                {busy ? <span className="spinner" /> : unsaved.dayFinished ? 'Save it under today' : 'Try saving it again'}
              </button>
              <button
                className="btn btn-ghost btn-block"
                onClick={() => {
                  setUnsaved(null);
                  setError(null);
                }}
                disabled={busy}
              >
                Let this recording go
              </button>
            </section>
          )}
          <VoiceRecorder onComplete={(rec) => void saveRecording(rec)} disabled={busy || unsaved !== null} />
          {busy && (
            <p className="faint" style={{ textAlign: 'center' }}>
              Saving…
            </p>
          )}
          {/* Set expectations before they record, not after. */}
          <div className="banner">
            {/* Three different absences, three different sentences. Explaining
                that a feature "isn't set up" to somebody who deliberately
                switched it off describes their own decision back to them as a
                fault. */}
            {/* "Written down" and "written up" are two different rungs, and
                this said the higher one at both. At `transcribe` the words are
                transcribed and nothing else happens to them — that is the whole
                promise of the setting — so telling somebody their entry will be
                "written up" describes a title and a recap they are not going to
                get, in the app's own vocabulary for the rung above theirs. */}
            {canTranscribe
              ? canSummarise
                ? 'Your recording is saved first, then written up in the background. It takes a moment for a long one — you can leave this screen.'
                : 'Your recording is saved first, then written down word for word in the background. Nothing is summarised. It takes a moment for a long one — you can leave this screen.'
              : turnedOff
                ? 'This will be saved as audio you can play back. Nothing is written up — AI is off.'
                : "Transcription isn't set up on this server, so this will be saved as audio you can play back. Add an OpenAI key to have it written up automatically."}
          </div>
        </>
      ) : (
        <>
          <div className="field">
            <label htmlFor="entry" className="sr-only">
              Your entry
            </label>
            <textarea
              id="entry"
              className="textarea"
              style={{ minHeight: 220 }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              /* Not a worked example. This one named a real person and their
                 real situation, which is the same fault as the life-event
                 field's, one screen over — and a demonstration of a good entry
                 quietly sets a bar for what is worth writing, on the screen
                 whose entire job is to be easy to start. */
              placeholder="Whatever's in your head. It doesn't have to be tidy."
              autoFocus
            />
          </div>
          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={() => void saveText()}
            disabled={busy || !text.trim()}
          >
            {busy ? <span className="spinner" /> : 'Save entry'}
          </button>
        </>
      )}
    </div>
  );
}

/** The weekday, so the choice reads as Saturday or Sunday and not as two dates. */
function dayName(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' });
}

function clockNow(): string {
  return new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
